import { sanitizeFileName } from "@/features/attachments/attachments.utils";
import { supabase } from "@/lib/supabase";
import type {
  AddAiMemberInput,
  AiAssistantKbItem,
  AiMember,
  AiPersona,
  AiPersonaUpdate,
  CreateAiPersonaInput,
  RemoveAiMemberInput,
  SharedWith,
} from "@shared/types/supabase.types";

export async function getAiPersonas(
  sharedWith?: SharedWith,
  containerId?: string,
): Promise<AiPersona[]> {
  let aiPersonaIds: string[] | null = null;

  if (containerId) {
    const { data: members, error: membersError } = await supabase
      .from("container_members_ai")
      .select("ai_persona_id")
      .eq("container_id", containerId);

    if (membersError) {
      throw new Error(
        `Failed to fetch AI members for container ${containerId}: ${membersError.message}`,
      );
    }

    const ids = members.map((member) => member.ai_persona_id).filter(Boolean);
    aiPersonaIds = Array.from(new Set(ids));

    if (aiPersonaIds.length === 0) {
      return [];
    }
  }

  let query = supabase.from("ai_personas").select("*");

  if (sharedWith) {
    query = query.eq("shared_with", sharedWith);
  }

  if (aiPersonaIds) {
    query = query.in("id", aiPersonaIds);
  }

  const { data, error } = await query;

  if (error) {
    const sharedWithLabel = sharedWith ? ` (${sharedWith})` : "";
    const containerLabel = containerId ? ` for container ${containerId}` : "";
    throw new Error(
      `Failed to fetch ${sharedWithLabel} AI assistant ${containerLabel}: ${error.message}`,
    );
  }

  return data;
}

export async function createAiPersona(input: CreateAiPersonaInput): Promise<AiPersona> {
  const { data, error } = await supabase.rpc("create_ai_persona", input);

  if (error) {
    throw new Error(`Failed to create AI assistant: ${error.message}`);
  }

  return data;
}

export async function addAiMember(input: AddAiMemberInput): Promise<AiMember> {
  const { data, error } = await supabase.rpc("add_ai_member", input);

  if (error) {
    throw new Error(`Failed to add AI member: ${error.message}`);
  }

  return data as AiMember;
}

export async function removeAiMember(input: RemoveAiMemberInput) {
  const { error } = await supabase.rpc("remove_ai_member", input);

  if (error) {
    throw new Error(`Failed to remove AI member: ${error.message}`);
  }

  return true;
}

export async function deleteAiPersona(id: string) {
  const { error } = await supabase.from("ai_personas").delete().eq("id", id);

  if (error) {
    throw new Error(`Failed to delete AI assistant`);
  }
  return true;
}

/**
 * What `p_model` must be to CLEAR a persona's model (CR-58).
 *
 * `update_ai_persona` (migration 20260421132720) applies every field as
 * `coalesce(p_model, ap.model)`, so NULL and `undefined` both mean "leave it
 * alone" — there is no argument that means "unset" and no `nullif` on this
 * parameter (unlike `create_ai_persona`, which does `nullif(trim(p_model),
 * '')`). The empty string is the only value that reaches the column and reads
 * as "nothing selected": the server's resolution chain rejects blanks
 * (`usableId` in api-server lib/model-resolution.ts), and no catalog row can
 * equal it because `ai_models_id_shape_check` forbids a blank id. So a cleared
 * persona falls through to the project default, and the picker — which shows
 * any non-catalog value as inherit — renders it as "Project default".
 */
const CLEAR_MODEL = "";

export async function updateAiPersona(id: string, input: AiPersonaUpdate): Promise<AiPersona> {
  const { data, error } = await supabase.rpc("update_ai_persona", {
    p_ai_persona_id: id,
    p_display_name: input.display_name,
    p_handle: input.handle,
    p_description: input.description || undefined,
    p_system_prompt: input.system_prompt,
    // Three distinct meanings, so `|| undefined` will not do: absent = don't
    // touch it, null = clear it, a string = set it.
    p_model: input.model === undefined ? undefined : (input.model ?? CLEAR_MODEL),
    p_avatar_url: input.avatar_url || undefined,
  });

  if (error) {
    throw new Error(`Failed to update AI assistant: ${error.message}`);
  }

  return data;
}

// ---------------------------------------------------------------------------
// AI Assistant Knowledge Base
// ---------------------------------------------------------------------------

const KB_BUCKET = "ai-assistant-knowledge-base";

export async function getAiAssistantKbItems(
  aiPersonaId: string,
  projectId?: string,
): Promise<AiAssistantKbItem[]> {
  let query = supabase
    .from("ai_assistant_knowledge_base_items")
    .select("*")
    .eq("ai_persona_id", aiPersonaId)
    .order("created_at", { ascending: true });

  if (projectId) {
    query = query.eq("project_id", projectId);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Failed to fetch knowledge base items: ${(error as Error).message}`);
  }

  return data;
}

export async function uploadAiAssistantKbItem({
  aiPersonaId,
  file,
  actorId,
  projectId,
}: {
  aiPersonaId: string;
  file: File;
  actorId: string;
  projectId?: string;
}): Promise<AiAssistantKbItem> {
  const storagePath = projectId
    ? `shared_with_all/${aiPersonaId}/${projectId}/${Date.now()}-${sanitizeFileName(file.name)}`
    : `custom/${aiPersonaId}/${Date.now()}-${sanitizeFileName(file.name)}`;

  const { error: uploadError } = await supabase.storage
    .from(KB_BUCKET)
    .upload(storagePath, file as unknown as Blob, {
      contentType: file.type,
      upsert: false,
    });

  if (uploadError) {
    throw new Error(`Failed to upload knowledge base file: ${uploadError.message}`);
  }

  const { data, error: insertError } = await supabase.rpc("insert_ai_assistant_kb_item", {
    p_ai_persona_id: aiPersonaId,
    p_storage_path: storagePath,
    p_file_name: sanitizeFileName(file.name),
    p_mime_type: file.type,
    p_size_bytes: file.size,
    p_created_by: actorId,
    p_project_id: projectId ?? null,
  });

  if (insertError) {
    // Best-effort cleanup of the uploaded file
    await supabase.storage.from(KB_BUCKET).remove([storagePath]);
    throw new Error(`Failed to register knowledge base file: ${(insertError as Error).message}`);
  }

  return data as AiAssistantKbItem;
}

export async function deleteAiAssistantKbItem(id: string): Promise<void> {
  // Fetch storage path first
  const { data: item, error: fetchError } = await supabase
    .from("ai_assistant_knowledge_base_items")
    .select("storage_path")
    .eq("id", id)
    .single();

  if (fetchError) {
    throw new Error(`Failed to find knowledge base item: ${fetchError.message}`);
  }

  // Remove from storage
  const { error: storageError } = await supabase.storage
    .from(KB_BUCKET)
    .remove([(item as { storage_path: string }).storage_path]);

  if (storageError) {
    throw new Error(`Failed to remove knowledge base file: ${storageError.message}`);
  }

  // Delete row
  const { error: deleteError } = await supabase.rpc("delete_ai_assistant_kb_item", {
    p_id: id,
  });

  if (deleteError) {
    throw new Error(`Failed to delete knowledge base item: ${(deleteError as Error).message}`);
  }
}
